import React, { useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { formatScheduledPublishDate } from "../../utils/Formatter"
import { BASE_URL, fetcher } from "../../utils/api"
import Spinner from "../../components/Spinner"
import DeleteModal from "../../components/DeleteModal"
import { ErrorNotification, SuccessNotification } from "../../utils"
import { FiEye, FiPlusCircle, FiTrash2 } from "react-icons/fi"
import { IoArrowForward, IoArrowBack } from "react-icons/io5"
import useSWR from 'swr'
import DashboardHeader from "../../components/DashboardHeader"
import { RxPencil1 } from "react-icons/rx"
import { Tooltip } from "../../layout/sidebar"
import { DateRangePickerDropdown } from "../../components/DashboardOverviewFilter"
import RenderNoCampaign from "../../components/NoData/RenderNoCampaign"
import CampaignDetailModal from "../../components/modals/CampaignDetailModal"
import NewCampaignModal from "../../components/modals/NewCampaignModal"
import EditCampaignModal from "../../components/modals/EditCampaignModal"
import SMSSenderModal from "../../components/modals/SMSSenderModal"
import useAuth from "../../context/userContext"
const TABS = [
  { key: "all", label: "All" },
  { key: "scheduled", label: "Scheduled Campaigns" },
  { key: "sent", label: "Sent Campaigns" },
]

const PAGE_SIZE = 10

const AllSentSMS = () => {
  const { user } = useAuth()
  const history = useHistory()
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState("")
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [selected, setSelected] = useState(null)
  const [activeTab, setActiveTab] = useState("all")
  const [dateRange, setDateRange] = useState({ start: null, end: null })
  const [newAddModal, setAddNewModal] = useState(false)
  const [openDetailModal, setOpenDetailModal] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showSmsModal, setShowSmsModal] = useState(false)
  const [editModal, setEditModal] = useState(false)
  const [messageType, setMessageType] = useState('all')
  const [selectedIds, setSelectedIds] = useState([])
  const [pendingDeleteIds, setPendingDeleteIds] = useState([])
  const { data: smsSenderIdsData, isLoading: loadingSenderIds, mutate: mutateSenderIds } = useSWR(`sms-sender-ids/`, fetcher)
  const smsSenderIds = Array.isArray(smsSenderIdsData) ? smsSenderIdsData : []
  const approvedSenderIds = useMemo(() =>
    smsSenderIds.filter(id => id.status === 'approved'),
    [smsSenderIds]
  )
  const nonApprovedSenderIds = useMemo(() =>
    smsSenderIds.filter(id => id.status !== 'approved'),
    [smsSenderIds]
  )

  // Get the default sender ID (first approved and enabled, or first approved, or first non-approved)
  const defaultSenderId = useMemo(() =>
    approvedSenderIds[0]?.effective_sender_id || "Solidaridad",
    [approvedSenderIds]
  )


  // SWR fetch
  const { data: campaigns, isLoading, error, mutate } = useSWR(`campaigns/`, fetcher)

  // --- Search and Filtering ---
  const filteredCampaigns = useMemo(() => {
    const now = new Date()
    const startDate = dateRange?.start ? new Date(new Date(dateRange.start).setHours(0, 0, 0, 0)) : null
    const endDate = dateRange?.end ? new Date(new Date(dateRange.end).setHours(23, 59, 59, 999)) : null

    return (campaigns?.results || [])
      ?.filter(c => {
        const isScheduled = c?.status === 'scheduled' || (c?.scheduled_publish_date && new Date(c.scheduled_publish_date) > now)
        const isSent = c?.status === 'sent' || c?.status === 'published' || c?.status === 'delivered' || !c?.scheduled_publish_date || new Date(c.scheduled_publish_date) <= now

        return (
          (!search ||
            c?.campaign_id?.toLowerCase().includes(search.toLowerCase()) ||
            c?.message_title?.toLowerCase().includes(search.toLowerCase()) ||
            c?.sms_nickname?.toLowerCase().includes(search.toLowerCase())
          ) &&
          (
            activeTab === 'all'
              ? true
              : activeTab === 'scheduled'
                ? isScheduled
                : isSent
          ) && (
            !startDate || !endDate || (
              (() => {
                const sched = new Date(c?.scheduled_publish_date || c?.created_at)
                return sched >= startDate && sched <= endDate
              })()
            )
          ) &&
          (messageType === 'all' || (c.message_type && c.message_type.toLowerCase() === messageType))
        )
      })
  }, [campaigns, search, activeTab, dateRange, messageType])

  // --- Pagination ---
  const totalPages = Math.ceil(filteredCampaigns.length / PAGE_SIZE)
  const paginatedCampaigns = filteredCampaigns.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  // --- UI Handlers ---
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
    setSelectedIds([])
  }

  const pageCampaignIds = useMemo(
    () => paginatedCampaigns.map((campaign) => campaign.id),
    [paginatedCampaigns]
  )

  const allPageSelected =
    pageCampaignIds.length > 0 && pageCampaignIds.every((id) => selectedIds.includes(id))

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds((prev) => [...new Set([...prev, ...pageCampaignIds])])
      return
    }

    const pageIdSet = new Set(pageCampaignIds)
    setSelectedIds((prev) => prev.filter((id) => !pageIdSet.has(id)))
  }

  const toggleSelectId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    )
  }

  const openDeleteModal = (ids) => {
    if (!ids.length) return
    setPendingDeleteIds(ids)
    setShowConfirmModal(true)
    setEditModal(false)
    setOpenDetailModal(false)
  }

  const handleDelete = async () => {
    if (!pendingDeleteIds.length) return

    setDeleting(true)
    try {
      const token = localStorage.getItem("token")
      const results = await Promise.all(
        pendingDeleteIds.map((id) =>
          fetch(`${BASE_URL}campaigns/${id}/`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          })
        )
      )

      const failedCount = results.filter((res) => !res.ok).length
      if (failedCount > 0) {
        throw new Error(`Failed to delete ${failedCount} campaign(s)`)
      }

      mutate()
      setSelectedIds((prev) => prev.filter((id) => !pendingDeleteIds.includes(id)))
      SuccessNotification(
        pendingDeleteIds.length === 1
          ? "Campaign deleted successfully"
          : `${pendingDeleteIds.length} campaigns deleted successfully`
      )
      setShowConfirmModal(false)
      setPendingDeleteIds([])
    } catch (error) {
      ErrorNotification(error?.message || "Failed to delete campaign")
    } finally {
      setDeleting(false)
    }
  }

  // --- Reset Filters ---
  const resetFilters = () => {
    setSearch("")
    setDateRange({ start: null, end: null })
    setActiveTab("all")
    setCurrentPage(1)
    setMessageType('all')
    setSelectedIds([])
  }

  // --- Header and Actions ---
  const Header = () => (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4 w-full">
      <div className="flex flex-col gap-1">
        <DashboardHeader label="Campaigns" />
        <div className="flex items-center gap-2 text-sm dark:text-white">
          SMS Nickname:
          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs font-semibold ml-1">
            {defaultSenderId}
          </span>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-3 md:items-center">
        {selectedIds.length > 0 && (
          <button
            onClick={() => openDeleteModal(selectedIds)}
            className="bg-red-500 hover:bg-red-600 text-white px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md font-medium shadow-sm"
          >
            <FiTrash2 size={20} />
            Delete Selected ({selectedIds.length})
          </button>
        )}
        <button
          onClick={() => setAddNewModal(true)}
          className="bg-yellowcolor hover:bg-yellowcolor/80 text-black px-6 flex flex-row items-center gap-2 py-2 capitalize rounded-md font-medium shadow-sm"
        >
          <FiPlusCircle size={20} />
          Create New Campaign
        </button>
      </div>
    </div>
  )

  // --- Tabs ---
  const Tabs = () => (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between w-full gap-5">
      <div className="flex flex-row flex-wrap border dark:border-border-dark  w-fit rounded-md overflow-hidden">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className={`px-6 w-full md:w-fit py-2 font-normal text-sm border dark:border-border-dark transition-all duration-150
              ${activeTab === tab.key
                ? 'bg-white text-black dark:text-white border-gray-200 dark:bg-black'
                : 'bg-gray-100 text-gray-500 border-gray-100 dark:bg-secondaryDark'}
            `}
            style={{ minWidth: 180 }}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex flex-row gap-2 items-center w-full md:w-auto">
        <div className="flex flex-row items-center  w-full justify-between gap-5">
          <select
            value={messageType}
            onChange={e => {
              setMessageType(e.target.value)
              setCurrentPage(1)
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellowcolor bg-white dark:border-border-dark dark:bg-dark dark:text-white"
            style={{ minWidth: 120 }}
          >
            <option value="all">Message Type</option>
            <option value="sms">SMS</option>
            <option value="audio">VOICE</option>
          </select>

          <DateRangePickerDropdown
            range={dateRange?.start && dateRange?.end ? [{ startDate: dateRange?.start, endDate: dateRange?.end, key: 'selection' }] : undefined}
            onChange={(rangeArr) => {
              setDateRange({ start: rangeArr[0]?.startDate, end: rangeArr[0]?.endDate })
              setCurrentPage(1)
            }}
          />
        </div>

        {(search.length > 0 || (dateRange?.start && dateRange?.end) || activeTab !== "all" || messageType !== 'all') && (
          <button
            onClick={resetFilters}
            className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md text-sm"
          >
            Reset
          </button>
        )}
      </div>
    </div>
  )

  // --- Table ---
  const CampaignsTable = () => (
    <div className="w-full min-w-0">
      <div className="">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-yellowcolor text-base font-semibold">
            {activeTab === 'all'
              ? 'All Campaigns'
              : activeTab === 'scheduled'
                ? 'Scheduled Campaigns'
                : 'Sent Campaigns'}
          </span>
          {selectedIds.length > 0 && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedIds.length} selected
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 bg-white dark:bg-dark border dark:border-border-dark dark:text-white bg-clip-border rounded-lg shadow-sm">
        <div className="overflow-x-auto w-full" style={{ WebkitOverflowScrolling: 'touch' }}>
          <table className="min-w-[900px] w-full divide-y divide-gray-200 dark:divide-border-dark">
            <thead className="bg-transparent dark:bg-secondaryDark">
              <tr>
                <th className="px-4 py-3 text-start text-sm font-light text-gray-500">
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={(e) => toggleSelectAll(e.target.checked)}
                    className="w-4 h-4 text-yellowcolor bg-gray-100 border-gray-300 rounded focus:ring-yellowcolor"
                  />
                </th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">ID</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Message Type</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Title</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Repeat</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Recipients</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Next Scheduled</th>
                <th className="px-6 py-3 text-start text-sm font-light text-gray-500 capitalize">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y bg-white divide-gray-200 dark:bg-dark dark:divide-border-dark">
              {paginatedCampaigns.map((data) => (
                <tr key={data.id} className="text-[13px]">
                  <td className="px-4 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(data.id)}
                      onChange={() => toggleSelectId(data.id)}
                      className="w-4 h-4 text-yellowcolor bg-gray-100 border-gray-300 rounded focus:ring-yellowcolor"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">{data?.campaign_id}</td>
                  <td className="px-6 py-4 uppercase whitespace-nowrap">{data?.message_type === "audio" ? "voice" : data?.message_type}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{data?.message_title}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{data?.schedule_type || '-'}</td>
                  <td className="px-6 py-4 text-center whitespace-nowrap">{data?.recipients_count}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{formatScheduledPublishDate(data?.scheduled_publish_date)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-center font-light">
                    <div className="flex flex-row items-center gap-4">
                      <Tooltip text="View" position="top">
                        <button
                          onClick={() => {
                            setSelected(data)
                            setOpenDetailModal(true)
                            setEditModal(false)
                          }}
                        >
                          <FiEye size={16} />
                        </button>
                      </Tooltip>
                      {/* {data?.status !== "published" && data?.status !== "sent" && data?.status !== "delivered" && <> */}
                      <Tooltip text="Edit" position="top">
                        <button
                          onClick={() => {
                            setSelected(data)
                            setEditModal(true)
                            setOpenDetailModal(false)
                          }}
                        >
                          <RxPencil1 size={16} />
                        </button>
                      </Tooltip>
                      <Tooltip text="Delete" position="top">
                        <button
                          onClick={() => {
                            setSelected(data)
                            openDeleteModal([data.id])
                          }}
                        >
                          <svg width="21" height="20" viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 4.98307C15.225 4.70807 12.4333 4.56641 9.65 4.56641C8 4.56641 6.35 4.64974 4.7 4.81641L3 4.98307" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M7.58398 4.14102L7.76732 3.04935C7.90065 2.25768 8.00065 1.66602 9.40898 1.66602H11.5923C13.0007 1.66602 13.109 2.29102 13.234 3.05768L13.4173 4.14102" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M16.2077 7.61523L15.666 16.0069C15.5744 17.3152 15.4993 18.3319 13.1743 18.3319H7.82435C5.49935 18.3319 5.42435 17.3152 5.33268 16.0069L4.79102 7.61523" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M9.10742 13.75H11.8824" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M8.41602 10.416H12.5827" stroke="#FE2B2B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </Tooltip>
                      {/* </>} */}

                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="bg-white border-t border-gray-200 p-3 flex flex-row items-center justify-between w-full dark:bg-dark dark:border-border-dark">
          <button
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300 dark:bg-secondaryDark dark:border-border-dark dark:text-white ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <IoArrowBack size={16} />
            Previous
          </button>
          <div className="">
            <ul className="flex flex-row items-center gap-0.5">
              {Array.from({ length: totalPages }).map((_, idx) => (
                <li key={idx}>
                  <button
                    className={`mx-1 flex h-9 w-9 items-center justify-center rounded-full p-0 text-sm text-blue-gray-500 transition duration-150 ease-in-out dark:bg-secondaryDark dark:border-border-dark dark:text-white ${currentPage === idx + 1 ? "bg-gray-100" : "bg-transparent"}`}
                    onClick={() => setCurrentPage(idx + 1)}
                  >
                    {idx + 1}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-4 py-1.5 flex flex-row items-center gap-2 bg-gray-100 hover:bg-gray-200 border-md text-black border rounded-lg font-light text-sm border-gray-300 dark:bg-secondaryDark dark:border-border-dark dark:text-white ${(currentPage === totalPages || totalPages === 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Next
            <IoArrowForward size={16} />
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <>
      {openDetailModal && <CampaignDetailModal
        data={selected}
        onClose={() => setOpenDetailModal(false)}
        onEdit={() => {
          setOpenDetailModal(false)
          setEditModal(true)
        }}
      />}

      {newAddModal && <NewCampaignModal
        onClose={() => setAddNewModal(false)}
        mutate={mutate}
        openSMSModal={() => {
          setAddNewModal(false)
          setShowSmsModal(true)
        }}
      />}

      {showSmsModal && <SMSSenderModal isOpen={showSmsModal} setIsOpen={() => setShowSmsModal(false)} />}

      {editModal && selected && (
        <EditCampaignModal
          campaignId={selected?.id}
          onClose={() => setEditModal(false)}
          mutate={mutate}
        />
      )}

      {showConfirmModal && <DeleteModal
        setShowConfirmModal={(open) => {
          setShowConfirmModal(open)
          if (!open && !deleting) setPendingDeleteIds([])
        }}
        showConfirmModal={showConfirmModal}
        title="campaign"
        message={
          pendingDeleteIds.length > 1
            ? `Are you sure you want to delete ${pendingDeleteIds.length} campaigns?`
            : undefined
        }
        loading={deleting}
        handleDelete={handleDelete}
      />}

      <div className="w-full lg:px-10 px-2 h-full py-10 space-y-4">
        <Header />
        <Tabs />
        {isLoading ? (
          <div className="h-96 grid items-center justify-center">
            <Spinner />
          </div>
        ) : paginatedCampaigns.length > 0 ? (
          <>
            <CampaignsTable />
          </>
        ) : (
          <RenderNoCampaign
            handleNewGroup={() => setAddNewModal(true)}
            isSearch={(search.length > 0 || (dateRange?.start && dateRange?.end) || activeTab !== "all")}
          />
        )}
      </div>
    </>
  )
}


export default AllSentSMS